import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../utils/cn.js';

export interface BreadcrumbItem {
  label: string;
  /** Omit for a grouping label that has no page of its own (e.g. "AI Agents"), or for the current page. */
  to?: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Where this page sits. Template example: NextAdmin `tailgrids/core/breadcrumbs.tsx` with `dividerType="chevron"`:
 * muted crumbs, a chevron between them, the last crumb (the current page) in the primary text colour.
 */
export function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  if (items.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className={cn('min-w-0', className)}>
      <ol className="flex min-w-0 flex-wrap items-center gap-2">
        {items.map((item, index) => {
          const current = index + 1 === items.length;
          const classes = cn(
            'flex items-center gap-1 truncate text-sm font-medium',
            current ? 'text-text-primary' : 'text-text-tertiary',
          );
          return (
            <li key={`${item.label}-${item.to ?? ''}`} className="contents text-text-tertiary">
              {index > 0 && <ChevronRight className="size-4 shrink-0" aria-hidden="true" />}
              {item.to && !current ? (
                <Link to={item.to} className={cn(classes, 'transition-colors hover:text-text-primary')}>
                  {item.label}
                </Link>
              ) : (
                <span className={classes} aria-current={current ? 'page' : undefined}>
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
